import { useState } from "react";
import { toast } from "sonner";
import { Globe, Lock } from "@phosphor-icons/react";
import api from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const plural = (n, w = "recipe") => `${n} ${w}${n === 1 ? "" : "s"}`;

function Summary({ make, p }) {
  return (
    <ul className="text-sm text-rf-ink space-y-1 mt-2" data-testid="bulk-privacy-summary">
      <li data-testid="bulk-privacy-will-change"><b>{plural(p.will_change)}</b> will become {make === "public" ? "Public" : "Private"}</li>
      {p.secret > 0 && <li data-testid="bulk-privacy-secret">{plural(p.secret)} marked Secret will stay Secret</li>}
      {p.already > 0 && <li>{plural(p.already)} already {make === "public" ? "Public" : "Private"}</li>}
      {p.copies_skipped > 0 && <li data-testid="bulk-privacy-copies">{plural(p.copies_skipped)} copied from other cooks stay private (publish those one by one)</li>}
    </ul>
  );
}

export const BulkPrivacyButtons = ({ cookbookId, onDone }) => {
  const [make, setMake] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cookbooks/${cookbookId}/bulk-privacy`, { make: m, dry_run: true });
      setPreview(data);
      setMake(m);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't check this cookbook"); } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cookbooks/${cookbookId}/bulk-privacy`, { make });
      toast.success(`${plural(data.changed)} now ${make === "public" ? "Public" : "Private"}${data.secret ? ` · ${data.secret} Secret unchanged` : ""}`);
      setMake(null);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update recipes"); } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" data-testid="bulk-make-public-btn" className="btn-outline-ink" disabled={busy} onClick={() => open("public")}>
        <Globe size={18} /> Make all recipes Public
      </button>
      <button type="button" data-testid="bulk-make-private-btn" className="btn-outline-ink" disabled={busy} onClick={() => open("private")}>
        <Lock size={18} /> Make all recipes Private
      </button>
      <AlertDialog open={!!make} onOpenChange={(o) => !o && setMake(null)}>
        <AlertDialogContent data-testid="bulk-privacy-dialog" className="bg-rf-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-black text-rf-ink">
              Make all recipes {make === "public" ? "Public" : "Private"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-rf-ink-soft">
              {make === "public" ? "Public recipes appear in the Recipe Filer Directory for anyone to see." : "Private recipes are hidden from the Directory and strangers."} The cookbook&apos;s own setting won&apos;t change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {preview && <Summary make={make} p={preview} />}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-privacy-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-privacy-confirm" disabled={busy || !preview?.will_change}
                               onClick={(e) => { e.preventDefault(); apply(); }} className="btn-amber">
              {preview?.will_change ? `Change ${plural(preview.will_change)}` : "Nothing to change"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
